import { useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Trash2, Pencil, MoreVertical, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EquipmentForm } from "@/components/equipment/EquipmentForm";
import { EquipmentInstanceTable } from "@/components/equipment/EquipmentInstanceTable";
import { EquipmentContentList } from "@/components/equipment/EquipmentContentList";
import { EquipmentProjectsList } from "@/components/equipment/EquipmentProjectsList";
import { EquipmentImageGallery } from "@/components/equipment/EquipmentImageGallery";
import {
  useSingleEquipment,
  useUpdateEquipment,
  useSoftDeleteEquipment,
  useCreateEquipment,
  type EquipmentInsert,
} from "@/hooks/useEquipment";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { QueryResult } from "@/components/QueryResult";
import { EquipmentSimsTab } from "@/components/sims/EquipmentSimsTab";
import { useSimDevices } from "@/hooks/useSims";

const EquipmentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: equipment, isLoading, isError, error } = useSingleEquipment(id);
  const updateEquipment = useUpdateEquipment();
  useSetCurrentEntity("equipment", id, equipment?.name);
  const softDelete = useSoftDeleteEquipment();
  const createEquipment = useCreateEquipment();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: simDevices } = useSimDevices();
  const canHoldSims =
    !!equipment?.is_serialized &&
    simDevices.some((device) => device.equipmentID === id);

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_EQUIPMENT_TABS = [
    "information",
    "contents",
    "projects",
    "instances",
    "sims",
  ] as const;
  const tabParam = searchParams.get("tab");
  const currentTab =
    tabParam &&
    VALID_EQUIPMENT_TABS.includes(
      tabParam as (typeof VALID_EQUIPMENT_TABS)[number],
    )
      ? tabParam
      : "information";
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const headerTabs = useMemo(
    () => [
      { value: "information", label: "Information" },
      { value: "contents", label: "Contents" },
      { value: "projects", label: "Projects" },
      ...(equipment?.is_serialized
        ? [{ value: "instances", label: "Instances" }]
        : []),
      ...(canHoldSims ? [{ value: "sims", label: "SIM Cards" }] : []),
    ],
    [equipment?.is_serialized, canHoldSims],
  );

  usePageHeader({
    title: equipment?.name,
    tabs: headerTabs,
    currentTab,
    onTabChange: handleTabChange,
  });

  const handleUpdate = (data: EquipmentInsert, tagIds: string[]) => {
    if (!id) return;
    updateEquipment.mutate(
      { id, equipment: data, tagIds },
      { onSuccess: () => setIsEditDialogOpen(false) },
    );
  };

  const handleDelete = () => {
    if (!id) return;
    softDelete.mutate(id, {
      onSuccess: () => navigate("/equipment"),
    });
  };

  const handleDuplicate = () => {
    if (!equipment) return;
    const tagIds = equipment.tags?.map((tag) => tag.id) || [];
    createEquipment.mutate(
      {
        equipment: {
          // No number: the database assigns the next free equipment number
          name: `${equipment.name} (Copy)`,
          notes: equipment.notes,
          manufacturer: equipment.manufacturer,
          is_serialized: equipment.is_serialized,
          stock_quantity: equipment.stock_quantity,
          country_of_origin: equipment.country_of_origin,
          carnet_ata_description: equipment.carnet_ata_description,
          carnet_ata_value: equipment.carnet_ata_value,
          carnet_ata_weight_unit: equipment.carnet_ata_weight_unit,
        },
        tagIds,
      },
      {
        onSuccess: (newEquipment) => {
          navigate(`/equipment/${newEquipment.id}`);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading equipment...</p>
      </div>
    );
  }

  if (!equipment) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={equipment}
        entityName="equipment"
        backTo="/equipment"
        backLabel="Back to Equipment"
      >
        {null}
      </QueryResult>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/equipment")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{equipment.name}</h1>
            <p className="text-muted-foreground">
              {equipment.number}
              {equipment.manufacturer && ` • ${equipment.manufacturer}`}
            </p>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              data-testid="equipment-actions-menu"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem offlineEdit onClick={handleDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              offlineEdit
              onClick={() => setIsDeleteDialogOpen(true)}
              className="text-destructive focus:text-destructive"
              data-testid="delete-equipment-menu-item"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Equipment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will move the equipment to trash. You can restore it later
                if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                data-testid="confirm-delete-equipment"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="information">Information</TabsTrigger>
          <TabsTrigger value="contents">Contents</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          {equipment.is_serialized && (
            <TabsTrigger value="instances">Instances</TabsTrigger>
          )}
          {canHoldSims && <TabsTrigger value="sims">SIM Cards</TabsTrigger>}
        </TabsList>

        <TabsContent value="information" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Images</CardTitle>
            </CardHeader>
            <CardContent>
              <EquipmentImageGallery
                equipmentId={id!}
                images={equipment.images || []}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Equipment Details</CardTitle>
              {isMobile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditDialogOpen(true)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isMobile ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <span className="ml-2 font-medium">{equipment.name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Number:</span>
                    <span className="ml-2 font-medium">{equipment.number}</span>
                  </div>
                  {equipment.manufacturer && (
                    <div>
                      <span className="text-muted-foreground">
                        Manufacturer:
                      </span>
                      <span className="ml-2 font-medium">
                        {equipment.manufacturer}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 font-medium">
                      {equipment.is_serialized ? "Serialized" : "Inventoried"}
                    </span>
                  </div>
                  {!equipment.is_serialized && (
                    <div>
                      <span className="text-muted-foreground">
                        Stock Quantity:
                      </span>
                      <span className="ml-2 font-medium">
                        {equipment.stock_quantity}
                      </span>
                    </div>
                  )}
                  {equipment.notes && (
                    <div>
                      <span className="text-muted-foreground">Notes:</span>
                      <p className="mt-1">{equipment.notes}</p>
                    </div>
                  )}
                  {equipment.tags && equipment.tags.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Tags:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {equipment.tags.map((tag) => (
                          <Badge key={tag.id} variant="outline">
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <EquipmentForm
                  equipment={equipment}
                  onSubmit={handleUpdate}
                  onCancel={() => navigate("/equipment")}
                  isLoading={updateEquipment.isPending}
                />
              )}
            </CardContent>
          </Card>

          {/* Mobile Edit Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Equipment</DialogTitle>
              </DialogHeader>
              <EquipmentForm
                equipment={equipment}
                onSubmit={handleUpdate}
                onCancel={() => setIsEditDialogOpen(false)}
                isLoading={updateEquipment.isPending}
              />
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="contents" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <EquipmentContentList equipmentId={id!} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <EquipmentProjectsList equipmentId={id!} />
            </CardContent>
          </Card>
        </TabsContent>

        {equipment.is_serialized && (
          <TabsContent value="instances" className="mt-6">
            <Card>
              <CardContent className="pt-6">
                <EquipmentInstanceTable equipmentId={id!} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canHoldSims && (
          <TabsContent value="sims" className="mt-6">
            <EquipmentSimsTab
              equipmentId={id!}
              equipmentName={equipment.short_name?.trim() || equipment.name}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default EquipmentDetailPage;
